import type { OnPageTransitionEndSync } from "vike/types";
import { trackEvent } from '../utils/analytics/analytics';
import { resolveSpaJobCardTransition } from '../utils/view-transitions/job-card-transition';
import {
  dismissJobPreviewShell,
  scheduleJobPreviewShellFallbackDismiss,
  settleHistoryPlaceholder,
} from '../utils/view-transitions/job-preview-shell';
import { restoreSrpScrollPosition } from '../utils/navigation/srp-scroll-restoration';

/**
 * Determine page type and extract IDs from URL or page context
 */
function getPageMetadata(pageContext: any, pathname: string): {
  page_type: 'job_detail' | 'srp' | 'company_profile' | 'home' | 'other';
  job_id?: string;
  company_id?: string;
  srp_id?: string;
} {
  // Check for job detail page: /{locale}/job/{jobId}/{companyId}/{seoSlug}
  const jobMatch = pathname.match(/\/(?:en|fi|sv)\/(?:job|tyopaikka|jobb)\/([^\/]+)\/([^\/]+)/);
  if (jobMatch) {
    return {
      page_type: 'job_detail',
      job_id: jobMatch[1],
      company_id: jobMatch[2],
    };
  }

  // Company profile: /{locale}/company/…, /fi/yritys/…, /sv/arbetsgivare/…
  const companyMatch = pathname.match(
    /\/(?:en|fi|sv)\/(?:company|yritys|arbetsgivare)\/([^/]+)/i,
  );
  if (companyMatch) {
    return {
      page_type: 'company_profile',
      company_id: companyMatch[1],
    };
  }

  // Home / search results: the index page doubles as the SRP. Classify as 'srp'
  // only when a query or filters are present, 'home' otherwise. Query params on
  // unrelated pages must not classify them as search.
  const isJobsListingPage = pathname === '/' ||
    !!pathname.match(/^\/(?:en|fi|sv)\/?$/) ||
    pathname.includes('/jobs');

  if (isJobsListingPage) {
    // Generate SRP ID from query + filters if available
    let srpId: string | undefined;
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const query = params.get('query') || params.get('en_search[query]') || params.get('fi_hae[query]') || params.get('sv_sok[query]') || '';
      const filters: Record<string, any> = {};
      
      // Extract filters from URL params
      params.forEach((value, key) => {
        if (key.includes('[refinementList]')) {
          const match = key.match(/\[refinementList\]\[([^\]]+)\]/);
          if (match) {
            const filterKey = match[1];
            if (!filters[filterKey]) {
              filters[filterKey] = [];
            }
            filters[filterKey].push(value);
          }
        }
      });
      
      if (query || Object.keys(filters).length > 0) {
        const filtersStr = JSON.stringify(filters);
        const combined = `${query}|${filtersStr}`;
        let hash = 0;
        for (let i = 0; i < combined.length; i++) {
          const char = combined.charCodeAt(i);
          hash = ((hash << 5) - hash) + char;
          hash = hash & hash;
        }
        srpId = `srp_${Math.abs(hash).toString(36)}`;
      }
    }
    
    if (srpId) {
      return {
        page_type: 'srp',
        srp_id: srpId,
      };
    }
    return {
      page_type: 'home',
    };
  }

  return {
    page_type: 'other',
  };
}

export const onPageTransitionEnd: OnPageTransitionEndSync = (pageContext) => {
  // Mark navigation as complete - this will be picked up by NavigationContext
  // This runs on both server and client, but we only dispatch events on client
  if (typeof window === 'undefined') return;

  if ((window as any).__vikeNavigationState) {
    (window as any).__vikeNavigationState.isNavigating = false;
  }

  // Vike only calls this hook at the end of a client-side navigation, never after hydration, so
  // every call here is a real navigation. (This used to treat "no navigation has finished yet" as
  // the initial load, which made the first navigation after every fresh page load skip the
  // scroll-to-top, leave its card morph hanging until the 1.5 s safety timeout, and send no
  // spa_page_view.)
  {
    // Use 'auto' instead of 'instant' to prevent scrollbar flash
    window.scrollTo({ top: 0, behavior: 'auto' });

    // The new page (job page, or the SRP again on Back) has now actually rendered -- resolve any
    // pending same-document view transition started in +onPageTransitionStart.ts, so the browser
    // can take its "new" snapshot and animate. No-ops if no transition is in flight. On Back/Forward
    // the SRP results list gets its scroll position back first, so the "new" snapshot (and the
    // re-armed card the morph returns to) is the list where the visitor left it.
    const restoreScroll = pageContext.isHistoryNavigation ? restoreSrpScrollPosition() : Promise.resolve();
    restoreScroll.then(() => resolveSpaJobCardTransition());

    // Skeleton job page shell (job-preview-shell.ts): JobRouteContent removes it as the real job
    // page commits. Anything else (Back pressed while it was loading, the error page for a job
    // URL) removes it here; decided by the page actually rendered, not the URL. On the job page
    // itself this is only a fallback in case that layout effect never ran.
    settleHistoryPlaceholder();
    const renderedJobPage =
      String((pageContext as { pageId?: string }).pageId ?? '').includes('/job/') &&
      !(pageContext as { is404?: boolean }).is404 &&
      !(pageContext as { errorWhileRendering?: unknown }).errorWhileRendering;
    if (renderedJobPage) {
      scheduleJobPreviewShellFallbackDismiss(1000);
    } else {
      dismissJobPreviewShell();
    }
  }
  (window as any).__vikeHasNavigated = true;

  // Dispatch custom event that NavigationContext can listen to
  // Use a small delay to ensure the event is dispatched after React has mounted
  setTimeout(() => {
    window.dispatchEvent(new CustomEvent('vike:pageTransitionEnd'));
  }, 0);

  // Single spa_page_view per navigation: gtag('config') already reports the initial
  // load, so only client-side navigations (the only time this hook runs) send it.
  {
    const pathname = window.location.pathname;
    const metadata = getPageMetadata(pageContext, pathname);
    const locale = pageContext.locale || 'en';

    trackEvent({
      name: 'spa_page_view',
      params: {
        page_type: metadata.page_type,
        job_id: metadata.job_id,
        company_id: metadata.company_id,
        srp_id: metadata.srp_id,
        path: pathname,
        locale: locale,
      },
    });
  }
};
